import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient, rawRequest } from "@/api/client";
import type { components, paths } from "@/types/api";

type DepositBody =
  paths["/api/v1/contracts/{contract_id}/deposit"]["put"]["requestBody"]["content"]["application/json"];
type DeductionBody =
  paths["/api/v1/contracts/{contract_id}/deposit/deductions"]["post"]["requestBody"]["content"]["application/json"];

// TODO: replace with generated type after openapi regen
type DepositResponse = components["schemas"]["DepositResponse"];
// TODO: replace with generated type after openapi regen
type UpdateDeductionBody = {
  amount?: number;
  description?: string;
  deduction_type?: string;
};

const key = (contractId: string) => ["contracts", contractId, "deposit"] as const;

export function useDeposit(contractId: string) {
  return useQuery({
    queryKey: key(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/contracts/{contract_id}/deposit",
        { params: { path: { contract_id: contractId } } },
      );
      if (error) throw new Error("Failed to load deposit");
      return data ?? null;
    },
  });
}

export function useUpsertDeposit(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DepositBody) => {
      const { data, error } = await apiClient.PUT(
        "/api/v1/contracts/{contract_id}/deposit",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to save deposit");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}

export function useAddDeduction(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeductionBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/deposit/deductions",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to add deduction");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}

export function useUpdateDeduction(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      deductionId,
      body,
    }: {
      deductionId: string;
      body: UpdateDeductionBody;
    }) => {
      // TODO: replace with typed apiClient.PATCH after openapi regen exposes
      // /api/v1/contracts/{contract_id}/deposit/deductions/{deduction_id} PATCH.
      return rawRequest<DepositResponse>(
        "PATCH",
        `/api/v1/contracts/${contractId}/deposit/deductions/${deductionId}`,
        body,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}

export function useCloseDeposit(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/deposit/close",
        { params: { path: { contract_id: contractId } } },
      );
      if (error || !data) throw new Error("Failed to close deposit");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(contractId) }),
  });
}
